import { createFileRoute } from "@tanstack/react-router";
import { LegalPage, LegalSection } from "@/components/wallet/LegalPage";

export const Route = createFileRoute("/terms")({
  staticData: { sitemap: true },
  head: () => ({
    meta: [
      { title: "Terms of Service — Cobalt Wallet" },
      {
        name: "description",
        content:
          "Terms for using Cobalt Wallet, a non-custodial XRP Ledger wallet, including service fees, eligibility and limits of liability.",
      },
      { property: "og:title", content: "Terms of Service — Cobalt Wallet" },
      {
        property: "og:description",
        content: "Self-custody software terms: you control your keys, your transactions and your risk.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://cobaltwalletxrpl.com/terms" }],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <LegalPage
      eyebrow="Terms"
      title="Terms of Service"
      updated="30 August 2026"
      intro="By using Cobalt Wallet you agree to these terms. Cobalt Wallet is software you run yourself, not a financial service, exchange or custodian."
    >
      <LegalSection heading="1. Self-custody">
        <p>
          Cobalt Wallet provides an interface for generating keys, signing transactions and reading
          public XRP Ledger data. You are the sole custodian of your keys and recovery phrase. We
          cannot freeze, reverse, recover or refund any transaction or lost credential.
        </p>
      </LegalSection>

      <LegalSection heading="2. Eligibility and lawful use">
        <p>
          You must be of legal age in your jurisdiction and may not use the app where doing so
          violates applicable law or sanctions. You are responsible for your own tax and regulatory
          obligations.
        </p>
      </LegalSection>

      <LegalSection heading="3. Service fees">
        <p>
          Certain actions carry a Cobalt Wallet service fee, shown in full on the review screen
          before you sign and summarised in Settings. The fee is charged as a separate XRP payment
          only after the underlying XRPL transaction reaches a successful result. Network reserve and
          ledger fees are set by the XRP Ledger, not by us. Fees may change; the amount shown at
          signing time is the amount that applies.
        </p>
      </LegalSection>

      <LegalSection heading="4. Third-party data and assets">
        <p>
          Prices, ETF figures, token listings and metrics come from third-party sources and may be
          delayed, incomplete or wrong. Token or trustline suggestions are not endorsements. Verify
          every issuer address before trusting an asset.
        </p>
      </LegalSection>

      <LegalSection heading="5. No investment advice">
        <p>
          Nothing in the app is investment, legal, accounting or tax advice. Digital assets are
          volatile and you may lose the entire value of your holdings.
        </p>
      </LegalSection>

      <LegalSection heading="6. Software provided as is">
        <p>
          The app is provided "as is" and "as available", without warranties of any kind. To the
          maximum extent permitted by law, we are not liable for lost keys, lost funds, missed
          transactions, downtime, third-party node or data failures, or any indirect or consequential
          loss.
        </p>
      </LegalSection>

      <LegalSection heading="7. Your responsibilities">
        <p>
          Keep your recovery phrase offline and private, verify recipient addresses in full, keep
          your device and browser updated, and never share your phrase, 25th word or passcode with
          anyone — including anyone claiming to be support.
        </p>
      </LegalSection>

      <LegalSection heading="8. Changes">
        <p>
          These terms may be updated; continued use after an update constitutes acceptance. The
          update date at the top of this page reflects the current version.
        </p>
      </LegalSection>
    </LegalPage>
  );
}
